import Cookies from 'js-cookie'
import CryptoJS from 'crypto-js'
import SentryManager from './SentryManager'

interface CookieConsent {
  functional: boolean
  analytics: boolean
}

interface AuthData {
  token: string
  refreshToken: string
  expiration: string
  userId: string
  email: string
}

const ENCRYPTION_KEY =
  import.meta.env.VITE_COOKIE_ENCRYPTION_KEY ||
  'your_secret_encryption_key_here'

export const COOKIE_KEYS = {
  AGE_VERIFICATION: 'sg_age_verification',
  FUNCTIONAL: 'sg_functional_cookies',
  ANALYTICS: 'sg_analytics_cookies',
  LOGIN_HINT: 'sg_login_hint_hidden',
  AUTH: 'sg_auth',
  BANNER_HIDED: 'bannerHided',
} as const

const COOKIE_EXPIRY = {
  LONG: 365, // 1 year
  MEDIUM: 180, // 6 months
  SHORT: 7, // 7 days
  SESSION: undefined, // Session duration
} as const

const COOKIE_OPTIONS = {
  SECURE: {
    secure: true,
    sameSite: 'Strict' as const,
  },
  AUTH: {
    secure: true,
    sameSite: 'Strict' as const,
    httpOnly: true,
  },
} as const

class CookieManager {
  static getLanguage(): string | null {
    try {
      return Cookies.get('i18next') || null
    } catch (error) {
      console.error('Error getting language cookie:', error)
      return null
    }
  }
  static setBannerHided(): void {
    Cookies.set('bannerHided', 'true', {
      expires: COOKIE_EXPIRY.SHORT,
      path: '/',
      ...COOKIE_OPTIONS.SECURE,
    })
  }

  static setLanguage(language: string): void {
    try {
      Cookies.set('i18next', language, {
        expires: 30,
        path: '/',
        ...COOKIE_OPTIONS.SECURE,
      })
    } catch (error) {
      console.error('Error setting language cookie:', error)
    }
  }

  static setAnalyticsCookie(enabled: boolean): void {
    if (enabled) {
      Cookies.set(COOKIE_KEYS.ANALYTICS, 'true', {
        expires: COOKIE_EXPIRY.LONG,
        ...COOKIE_OPTIONS.SECURE,
      })
    } else {
      Cookies.remove(COOKIE_KEYS.ANALYTICS)
    }
  }
  static initializeCookies(consent: CookieConsent): void {
    if (consent.functional) {
      Cookies.set(COOKIE_KEYS.FUNCTIONAL, 'true', {
        expires: COOKIE_EXPIRY.LONG,
        ...COOKIE_OPTIONS.SECURE,
      })
    }

    if (consent.analytics) {
      Cookies.set(COOKIE_KEYS.ANALYTICS, 'true', {
        expires: COOKIE_EXPIRY.LONG,
        ...COOKIE_OPTIONS.SECURE,
      })
      const sentryManager = SentryManager.getInstance()
      sentryManager.init()
    } else {
      Cookies.remove(COOKIE_KEYS.ANALYTICS)
      const sentryManager = SentryManager.getInstance()
      sentryManager.shutdown()
    }
  }

  static setAgeVerification(): void {
    Cookies.set(COOKIE_KEYS.AGE_VERIFICATION, 'verified', {
      expires: COOKIE_EXPIRY.LONG,
      ...COOKIE_OPTIONS.SECURE,
    })
  }

  static isAgeVerified(): boolean {
    return Cookies.get(COOKIE_KEYS.AGE_VERIFICATION) === 'verified'
  }

  static getConsent(): CookieConsent {
    return {
      functional: Cookies.get(COOKIE_KEYS.FUNCTIONAL) === 'true',
      analytics: Cookies.get(COOKIE_KEYS.ANALYTICS) === 'true',
    }
  }

  static removeAllCookies(): void {
    Object.values(COOKIE_KEYS).forEach(key => {
      Cookies.remove(key)
    })

    const cookies = document.cookie.split(';')
    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim()
      if (
        cookieName.startsWith('_sentry') ||
        cookieName.startsWith('AMP_') ||
        cookieName.startsWith('sentry-') ||
        cookieName === 'gsID'
      ) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
      }
    })
  }

  static setLoginHintHidden(): void {
    Cookies.set(COOKIE_KEYS.LOGIN_HINT, 'true', {
      expires: COOKIE_EXPIRY.LONG,
      ...COOKIE_OPTIONS.SECURE,
    })
  }

  static isLoginHintHidden(): boolean {
    return Cookies.get(COOKIE_KEYS.LOGIN_HINT) === 'true'
  }

  private static encryptData(data: string): string {
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString()
  }

  private static decryptData(encryptedData: string): string | null {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY)
      return bytes.toString(CryptoJS.enc.Utf8)
    } catch {
      return null
    }
  }

  static setAuth(data: AuthData, rememberMe: boolean = false): void {
    const authString = JSON.stringify(data)
    const encryptedAuth = this.encryptData(authString)

    Cookies.set(COOKIE_KEYS.AUTH, encryptedAuth, {
      expires: rememberMe ? COOKIE_EXPIRY.LONG : COOKIE_EXPIRY.SHORT,
      ...COOKIE_OPTIONS.SECURE,
    })
  }

  static getAuth(): AuthData | null {
    const encryptedAuth = Cookies.get(COOKIE_KEYS.AUTH)
    if (!encryptedAuth) return null

    const decryptedAuth = this.decryptData(encryptedAuth)
    if (!decryptedAuth) return null

    try {
      return JSON.parse(decryptedAuth) as AuthData
    } catch {
      return null
    }
  }

  static clearAuth(): void {
    Cookies.remove(COOKIE_KEYS.AUTH)
  }

  static getToken(): string | null {
    const auth = this.getAuth()
    return auth?.token || null
  }

  static getRefreshToken(): string | null {
    const auth = this.getAuth()
    return auth?.refreshToken || null
  }

  static getEmail(): string | null {
    const auth = this.getAuth()
    return auth?.email || null
  }

  static isAuthenticated(): boolean {
    const auth = this.getAuth()
    if (!auth?.token || !auth?.expiration) return false
    return new Date(auth.expiration) > new Date()
  }

  static updateTokens(
    token: string,
    refreshToken: string,
    expiration: string,
  ): void {
    const auth = this.getAuth()
    if (auth) {
      this.setAuth({
        ...auth,
        token,
        refreshToken,
        expiration,
      })
    }
  }

  static getUserId(): string | null {
    const auth = this.getAuth()
    return auth?.userId || null
  }
}

export default CookieManager
